import { type Query, useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useSyncExternalStore } from "react";
import { useKeycloakToken } from "../common/hooks/useKeycloakToken";
import config from "../config";
import { subscribeToLiveUpdates } from "./subscribeToLiveUpdates";
import { getLiveUpdateTopic, type LiveUpdateTopic } from "./topics";

export function useLiveUpdates() {
    const queryClient = useQueryClient();
    const getKeycloakToken = useKeycloakToken();
    const queryCache = queryClient.getQueryCache();
    const topics = useSyncExternalStore(
        (onChange) => queryCache.subscribe(onChange),
        () => joinActiveTopics(queryCache.getAll()),
    );

    const getAccessToken = useEffectEvent(getKeycloakToken);

    useEffect(() => {
        if (!topics) {
            return;
        }

        const invalidateAll = () => {
            void queryClient.invalidateQueries({
                predicate: (query) => !!getLiveUpdateTopic(query.queryKey),
            });
        };

        const abortController = new AbortController();
        void subscribeToLiveUpdates({
            url: `${config.apiServerBaseUrl}/lmt/live-updates?topics=${encodeURIComponent(topics)}`,
            abortSignal: abortController.signal,
            getAccessToken,
            onUpdate: (updatedTopic) => {
                void queryClient.invalidateQueries({
                    predicate: (query) => isAffectedBy(query, updatedTopic),
                });
            },
            onResync: invalidateAll,
            onReconnectAfterError: invalidateAll,
        });

        return () => abortController.abort();
    }, [topics, queryClient]);
}

function joinActiveTopics(queries: Query[]): string {
    const topics = queries
        .filter((query) => query.isActive())
        .map((query) => getLiveUpdateTopic(query.queryKey))
        .filter((topic) => topic !== undefined)
        .map((topic) => (topic.id ? `${topic.name}:${topic.id}` : topic.name));
    return [...new Set(topics)].sort().join(",");
}

function isAffectedBy(query: Query, updatedTopic: LiveUpdateTopic): boolean {
    const queryTopic = getLiveUpdateTopic(query.queryKey);
    return (
        queryTopic?.name === updatedTopic.name &&
        (queryTopic.id === undefined || queryTopic.id === updatedTopic.id)
    );
}
